import React, { useEffect, useState } from 'react';
import { BriefIssue } from './BriefIssue';
import { issueTitle } from './BriefArchive';
import './brief.css';

const TOKEN_KEY = 'mb_admin_token';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, "X-API-Key": process.env.REACT_APP_API_KEY || "" };
}

function VerifierReport({ report }) {
  if (!report) return <div className="mb-empty">No verifier report for this draft yet.</div>;
  const inner = report.report || report;
  const passed = inner.passed;
  const errors = inner.errors || [];
  const warnings = inner.warnings || inner.flags || [];
  return <div className="mb-verdict-box">
    <h4>Verifier report{report.at ? ` · ${report.at}` : ''}{report.attempt != null ? ` · attempt ${report.attempt}` : ''}</h4>
    <p><strong>Status:</strong> {passed === true ? 'Passed' : passed === false ? 'Failed' : 'Unknown'}</p>
    {errors.length > 0 && <><p><strong>Errors</strong></p><ul>{errors.map((e, i) => <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>)}</ul></>}
    {warnings.length > 0 && <><p><strong>Warnings</strong></p><ul>{warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : JSON.stringify(w)}</li>)}</ul></>}
    {errors.length === 0 && warnings.length === 0 && <p style={{ color: '#6B7280' }}>No errors or warnings recorded.</p>}
    <details><summary style={{ cursor: 'pointer', fontSize: 13 }}>Raw report</summary><pre>{JSON.stringify(report, null, 2)}</pre></details>
  </div>;
}

export default function BriefAdmin() {
  const [token, setToken] = useState(() => (typeof window !== 'undefined' && window.sessionStorage.getItem(TOKEN_KEY)) || '');
  const [draftToken, setDraftToken] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [verifier, setVerifier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadDrafts = async (tok) => {
    setLoading(true); setError(''); setNotice('');
    try {
      const res = await fetch('/api/research/brief/admin/drafts', { headers: authHeaders(tok), cache: 'no-store' });
      if (res.status === 401) throw new Error('Invalid token. Check RESEARCH_ADMIN_TOKEN and try again.');
      if (!res.ok) throw new Error('Admin API unavailable. Please retry.');
      const data = await res.json();
      setDrafts(data.drafts || []);
    } catch (e) {
      setError(e.message || 'Failed to load drafts.');
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) loadDrafts(token); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = (e) => {
    e.preventDefault();
    const t = draftToken.trim();
    if (!t) return;
    window.sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setDraftToken('');
    setSelected(null);
    loadDrafts(t);
  };

  const signOut = () => {
    window.sessionStorage.removeItem(TOKEN_KEY);
    setToken(''); setDrafts([]); setSelected(null); setVerifier(null);
  };

  const openDraft = async (slug) => {
    setLoading(true); setError(''); setNotice(''); setVerifier(null);
    try {
      const [dRes, vRes] = await Promise.all([
        fetch(`/api/research/brief/admin/drafts/${encodeURIComponent(slug)}`, { headers: authHeaders(token) }),
        fetch(`/api/research/brief/admin/verifier/${encodeURIComponent(slug)}`, { headers: authHeaders(token) }),
      ]);
      if (dRes.status === 404) throw new Error('Draft not found.');
      if (!dRes.ok) throw new Error('Failed to load the draft.');
      const draft = await dRes.json();
      let vrep = null;
      if (vRes.ok) vrep = await vRes.json();
      setSelected(draft); setVerifier(vrep);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError(e.message || 'Failed to open the draft.');
    } finally {
      setLoading(false);
    }
  };

  const publish = async (slug) => {
    if (!window.confirm(`Publish "${slug}" to the live archive?`)) return;
    setLoading(true); setError(''); setNotice('');
    try {
      const res = await fetch(`/api/research/brief/admin/publish/${encodeURIComponent(slug)}`, { method: 'POST', headers: authHeaders(token) });
      if (!res.ok) throw new Error('Publish failed. Please retry.');
      setNotice(`Published ${slug}.`);
      setSelected(null); setVerifier(null);
      await loadDrafts(token);
    } catch (e) {
      setError(e.message || 'Publish failed.');
    } finally {
      setLoading(false);
    }
  };

  const discard = async (slug) => {
    if (!window.confirm(`Discard draft "${slug}"? This cannot be undone.`)) return;
    setLoading(true); setError(''); setNotice('');
    try {
      const res = await fetch(`/api/research/brief/admin/discard/${encodeURIComponent(slug)}`, { method: 'POST', headers: authHeaders(token) });
      if (!res.ok) throw new Error('Discard failed. Please retry.');
      setNotice(`Discarded ${slug}.`);
      setSelected(null); setVerifier(null);
      await loadDrafts(token);
    } catch (e) {
      setError(e.message || 'Discard failed.');
    } finally {
      setLoading(false);
    }
  };

  return <div className="mb"><div className="mb-col">
    <div className="mb-cover-kicker">AEON Research · Admin</div>
    <h1 className="mb-serif" style={{ fontWeight: 900, fontSize: 'clamp(26px,3.6vw,38px)', margin: '0 0 8px' }}>Market Brief drafts</h1>

    {!token && <form onSubmit={signIn} className="mb-admin-bar">
      <input className="mb-input" type="password" autoComplete="off" placeholder="RESEARCH_ADMIN_TOKEN"
        value={draftToken} onChange={e => setDraftToken(e.target.value)} aria-label="Admin token" />
      <button className="mb-btn" type="submit">Sign in</button>
    </form>}

    {token && <div className="mb-admin-bar">
      <button className="mb-btn" type="button" onClick={() => loadDrafts(token)} disabled={loading}>Refresh drafts</button>
      <button className="mb-btn" type="button" onClick={signOut}>Sign out</button>
    </div>}

    {notice && <div className="mb-update-note">{notice}</div>}
    {error && <div className="mb-error">{error}</div>}
    {loading && <div className="mb-empty">Working…</div>}

    {token && !selected && !loading && <>
      {drafts.length === 0 && <div className="mb-empty">No drafts waiting for review.</div>}
      <ul className="mb-draft-list">{drafts.map(d => <li key={d.slug} className="mb-draft-row">
        <div><strong>{issueTitle(d)}</strong><div className="mb-issue-meta"><span className="mb-tier-pill">{d.tier}</span><span>{d.date}</span><span className="mb-mono">{d.slug}</span></div></div>
        <button className="mb-btn" type="button" onClick={() => openDraft(d.slug)}>Review</button>
      </li>)}</ul>
    </>}

    {token && selected && <>
      <div className="mb-admin-bar">
        <button className="mb-btn" type="button" onClick={() => { setSelected(null); setVerifier(null); }}>← Back to drafts</button>
        <button className="mb-btn" type="button" onClick={() => publish(selected.slug)} disabled={loading}>Approve &amp; publish</button>
        <button className="mb-btn mb-btn-danger" type="button" onClick={() => discard(selected.slug)} disabled={loading}>Discard</button>
      </div>
      <VerifierReport report={verifier} />
      <div className="mb-cover-kicker" style={{ marginTop: 24 }}>Draft preview (read-only)</div>
      <BriefIssue issue={selected} />
    </>}
  </div></div>;
}
